// ---------------------------------------------------------------------------
// Admin, at /admin.
//
// Two rules shape this screen.
//
// 1. Admin mode is off by default even for a verified admin. Holding a valid
//    session token is not the same as wanting privileged information on screen:
//    the admin account plays ordinary games, and a room's drafts, IPs and device
//    ids must not be one render away from a game the same person is playing. So
//    `settingsStore.adminMode` gates every privileged panel and every privileged
//    request, and a badge says so while it is on.
//
// 2. Every destructive action takes two deliberate clicks. `kill-all` closes
//    every room on the server, and it sits on the same toolbar as `refresh`.
//
// The panels themselves live in `admin/`. This file is the gate, the polling
// load, and the wiring between them; everything it renders is a pure panel that
// takes props and hands an action back.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import type {
  AdminBan,
  AdminLlmUsage,
  AdminMetrics,
  AdminReport,
  AdminRoomDetail,
  AdminRoomSummary,
} from '@countdown/shared';
import { adminFetch } from '../lib/adminAuth.js';
import { useSettingsStore } from '../store/settingsStore.js';
import { Button, Card, CardTitle } from '../components/primitives/index.js';
import { AdminModeBadge, ConfirmAction, emptyBan } from './admin/atoms.js';
import type { BanDraft } from './admin/atoms.js';
import { SignIn } from './admin/SignIn.js';
import { LlmUsagePanel, Metrics } from './admin/Metrics.js';
import { RoomDetail, Rooms } from './admin/Rooms.js';
import { Bans, ForceRenameUser, Reports } from './admin/Moderation.js';
import { Maintenance } from './admin/Maintenance.js';

const POLL_MS = 5_000;

export function AdminScreen() {
  const token = useSettingsStore((s) => s.adminSessionToken);
  const setToken = useSettingsStore((s) => s.setAdminSessionToken);
  const adminMode = useSettingsStore((s) => s.adminMode);
  const setAdminMode = useSettingsStore((s) => s.setAdminMode);
  const [sessionNote, setSessionNote] = useState<string | null>(null);

  const open = Boolean(token) && adminMode;

  // Stable identity: an inline arrow here would be a new prop on every render of
  // this screen, which tears down and restarts the panels' polling effect.
  const handleSessionLost = useCallback(() => {
    setToken(null);
    setAdminMode(false);
    setSessionNote('That session expired. Sign in again.');
  }, [setToken, setAdminMode]);

  return (
    <main className="mx-auto w-full max-w-[960px] px-4 py-6 sm:px-6">
      {open && <AdminModeBadge />}

      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Admin</h1>
        {token && (
          <Button
            variant="outline"
            onClick={() => {
              setToken(null);
              setAdminMode(false);
            }}
          >
            Sign out
          </Button>
        )}
      </header>

      {sessionNote && (
        <p role="alert" className="mt-4 rounded-card border border-alert px-4 py-3 text-sm text-ink">
          {sessionNote}
        </p>
      )}

      {!token ? (
        <SignIn
          onSignedIn={(next) => {
            setToken(next);
            setSessionNote(null);
          }}
        />
      ) : (
        <div className="mt-5 flex flex-col gap-5">
          <Card>
            <CardTitle>Admin mode</CardTitle>
            <p className="mt-2 max-w-[520px] text-sm text-ink-muted text-pretty">
              Signed in. Nothing privileged loads until admin mode is on, so an
              ordinary game on this account looks like an ordinary game. A badge
              stays on screen the whole time it is on.
            </p>
            <div className="mt-3">
              <Button
                variant={adminMode ? 'outline' : 'primary'}
                onClick={() => setAdminMode(!adminMode)}
                aria-pressed={adminMode}
              >
                {adminMode ? 'Turn admin mode off' : 'Turn admin mode on'}
              </Button>
            </div>
          </Card>

          {/* Mounted only while the gate is open, so every privileged response
              lives in this subtree and is discarded with it. Nothing privileged
              can sit in state waiting to be rendered by accident. */}
          {open && token && <AdminPanels token={token} onSessionLost={handleSessionLost} />}
        </div>
      )}
    </main>
  );
}

function AdminPanels({ token, onSessionLost }: { token: string; onSessionLost: () => void }) {
  const [rooms, setRooms] = useState<AdminRoomSummary[]>([]);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [bans, setBans] = useState<AdminBan[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [llm, setLlm] = useState<AdminLlmUsage | null>(null);
  const [openRoom, setOpenRoom] = useState<AdminRoomDetail | null>(null);
  const [banDraft, setBanDraft] = useState<BanDraft>(emptyBan);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Bumped by anything that changed server state, to re-run the load below. */
  const [reloadCount, setReloadCount] = useState(0);

  /** Any 401 means the stored token has aged out. Drop it rather than retrying. */
  const handle = useCallback(
    (thrown: unknown) => {
      const message = thrown instanceof Error ? thrown.message : 'That request failed.';
      if (/unauthorized|\(401\)/i.test(message)) {
        onSessionLost();
        return;
      }
      setError(message);
    },
    [onSessionLost],
  );

  useEffect(() => {
    // `live` is what makes the gate real rather than cosmetic. A request in
    // flight when admin mode goes off has its response dropped here instead of
    // being written into state, so no privileged payload outlives this subtree.
    let live = true;

    const load = () => {
      Promise.all([
        adminFetch<AdminRoomSummary[]>('/rooms', token),
        adminFetch<AdminMetrics>('/metrics', token),
        adminFetch<AdminBan[]>('/bans', token),
        adminFetch<AdminReport[]>('/reports', token),
        adminFetch<AdminLlmUsage>('/llm-usage', token),
      ])
        .then(([nextRooms, nextMetrics, nextBans, nextReports, nextLlm]) => {
          if (!live) return;
          setRooms(nextRooms);
          setMetrics(nextMetrics);
          setBans(nextBans);
          setReports(nextReports);
          setLlm(nextLlm);
          setError(null);
        })
        .catch((thrown: unknown) => {
          if (live) handle(thrown);
        });
    };

    // The room list is the one panel that is worth polling: phases and player
    // counts change while it is open.
    let timer: number | null = null;
    const start = () => {
      if (timer === null) timer = window.setInterval(load, POLL_MS);
    };
    const stop = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    };

    // The same gate BrowseScreen polls behind, for the same reason and in the
    // same shape: a hidden tab is one nobody is reading, and five privileged
    // requests every five seconds into a tab left open in another window is the
    // server's time spent on nothing. Coming back reloads at once, so what is on
    // screen is never the state of the room list five minutes ago.
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else {
        load();
        start();
      }
    };

    load();
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      live = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [token, handle, reloadCount]);

  const refresh = useCallback(() => setReloadCount((count) => count + 1), []);

  const act = useCallback(
    async (run: (session: string) => Promise<string>) => {
      try {
        setNotice(await run(token));
        setError(null);
        refresh();
      } catch (thrown) {
        handle(thrown);
      }
    },
    [token, refresh, handle],
  );

  const openDetail = useCallback(
    async (code: string) => {
      try {
        setOpenRoom(await adminFetch<AdminRoomDetail>(`/rooms/${code}`, token));
      } catch (thrown) {
        handle(thrown);
      }
    },
    [token, handle],
  );

  return (
    <>
      {error && (
        <p role="alert" className="rounded-card border border-alert px-4 py-3 text-sm text-ink">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-card border border-line px-4 py-3 text-sm text-ink-muted">
          {notice}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={refresh}>
          Refresh
        </Button>
        <ConfirmAction
          label="Kill all rooms"
          question="Close every room on the server and send everybody home?"
          confirmLabel="Yes, close every room"
          onConfirm={() =>
            void act(async (session) => {
              const result = await adminFetch<{ closed: number }>('/rooms/kill-all', session, {
                method: 'POST',
              });
              return `Closed ${result.closed} rooms.`;
            })
          }
        />
      </div>

      {metrics && <Metrics metrics={metrics} />}
      {llm?.enabled && <LlmUsagePanel usage={llm} />}

      <Rooms
        rooms={rooms}
        onOpen={(code) => void openDetail(code)}
        onClose={(code) =>
          void act(async (session) => {
            await adminFetch(`/rooms/${code}/close`, session, { method: 'POST' });
            setOpenRoom(null);
            return `Closed room ${code}.`;
          })
        }
      />

      {openRoom && (
        <RoomDetail
          detail={openRoom}
          onDismiss={() => setOpenRoom(null)}
          onBanSubject={(draft) => setBanDraft(draft)}
          onForceRename={(playerId) =>
            void act(async (session) => {
              const result = await adminFetch<{ displayName: string | null }>(
                '/force-rename',
                session,
                {
                  method: 'POST',
                  body: JSON.stringify({
                    target: {
                      kind: 'player',
                      roomCode: openRoom.summary.roomCode,
                      playerId,
                    },
                    includeUsername: false,
                  }),
                },
              );
              return result.displayName
                ? `Renamed to ${result.displayName}.`
                : 'That player was already gone.';
            })
          }
        />
      )}

      <Bans
        bans={bans}
        draft={banDraft}
        onDraft={setBanDraft}
        onAdd={() =>
          void act(async (session) => {
            await adminFetch<AdminBan>('/bans', session, {
              method: 'POST',
              body: JSON.stringify({
                subjectKind: banDraft.subjectKind,
                subject: banDraft.subject.trim(),
                scope: banDraft.scope,
                roomCode: banDraft.scope === 'room' ? banDraft.roomCode.toUpperCase() : null,
                reason: banDraft.reason.trim() || null,
                expiresAt: null,
              }),
            });
            setBanDraft(emptyBan);
            return 'Ban added.';
          })
        }
        onLift={(id) =>
          void act(async (session) => {
            await adminFetch(`/bans/${id}`, session, { method: 'DELETE' });
            return 'Ban lifted.';
          })
        }
      />

      <Reports
        reports={reports}
        onResolve={(id) =>
          void act(async (session) => {
            await adminFetch(`/reports/${id}/resolve`, session, { method: 'POST' });
            return 'Report resolved.';
          })
        }
        onBanSubject={(draft) => setBanDraft(draft)}
      />

      <ForceRenameUser
        onRename={(userId, includeUsername) =>
          void act(async (session) => {
            const result = await adminFetch<{ displayName: string }>('/force-rename', session, {
              method: 'POST',
              body: JSON.stringify({ target: { kind: 'user', userId }, includeUsername }),
            });
            return `Renamed to ${result.displayName}.`;
          })
        }
      />

      <Maintenance
        onAnnounce={(message, delaySeconds) =>
          void act(async (session) => {
            await adminFetch('/maintenance', session, {
              method: 'POST',
              body: JSON.stringify({ message, delaySeconds }),
            });
            return `Announced to every room, landing in ${delaySeconds} seconds.`;
          })
        }
      />
    </>
  );
}
